import React, { useEffect } from 'react';
import { FunnelPage } from '../../redux/interfaces/mainData';
import { useWizard } from 'react-use-wizard';
import { addToStepHistory, getContainerFinalSectionId, setupHistoryBackForWizard } from '../helper';
import ProgressBar from '../../SharedUI/components/ProgressBar';
import {
  fireConversionsApi,
  fireFacebookPixel,
  fireGoogleTag,
  fireTikTokPixel,
  fireLinkedInEvent,
  fireMeetovoTracking,
} from '../../Pager/utils/tracking';
import ResolvedComponentsByCraftState from './ResolvedComponentsByCraftState';
import { useAppDispatch, useAppSelector } from '../../redux/root/hooks';
import {
  selectMainData,
  setNextPageActionLogic,
  setSectionPageActionLogic,
  useNextPageActionLogic,
  useNextSectionActionLogic,
} from '../../redux/slices/mainData';
import { factorsAiTrackEvent } from '../../helper/segmetrics';
import {
  checkType,
  doesPageExist,
  getFunnelPageIdByStepIndex,
  getStepIndexByFunnelPageId,
} from '../../Pager/helper';
import { useAuthenticatedSocket } from '../../helper/socket';

enum ACTION_LOGIC {
  LINKING = 'linking',
  PAGE_SECTION_LINK = 'pageSectionLink',
}

const Page = (funnelPage: FunnelPage): React.ReactElement => {
  const { factorsAiEnabled } = useAppSelector(selectMainData);
  const nextPageActionLogic = useNextPageActionLogic();
  const nextSectionActionLogic = useNextSectionActionLogic();
  const {
    customJs,
    facebookPixelEvent,
    googleTagTrigger,
    trackingId,
    tikTokPixelEvent,
    linkedInEvent,
    name,
  } = funnelPage;
  const { goToStep, nextStep, activeStep } = useWizard();

  const actionLogicType = checkType(nextPageActionLogic, 'number');
  const { socket } = useAuthenticatedSocket();
  const dispatch = useAppDispatch();

  useEffect(() => {
    window.goToStep = goToStep;
    setupHistoryBackForWizard();
  }, [goToStep]);

  useEffect(() => {
    fireFacebookPixel(facebookPixelEvent);
    fireTikTokPixel(tikTokPixelEvent, name);
    fireConversionsApi(facebookPixelEvent);
    fireGoogleTag(googleTagTrigger);
    fireLinkedInEvent(linkedInEvent);

    if (factorsAiEnabled) factorsAiTrackEvent('FUNNEL_PAGE_VIEWED');
  }, []);

  // HANDLING - ROUTING FOR PAGES
  useEffect(() => {
    if (!nextPageActionLogic) {
      return;
    }
    let actionLogic: any = nextPageActionLogic;
    if (actionLogicType) {
      const pageExists = doesPageExist(actionLogic as number);
      if (!pageExists) {
        actionLogic = 'next-page';
      }
    }

    if (actionLogic === ACTION_LOGIC.PAGE_SECTION_LINK) {
      if (nextSectionActionLogic) {
        document
          .getElementById(getContainerFinalSectionId(nextSectionActionLogic))
          ?.scrollIntoView();
        return;
      }
    }
    const nextIsBasedOnLogic = typeof actionLogic === 'number';
    const currentPageId = getFunnelPageIdByStepIndex(activeStep);
    const nextPageId = nextIsBasedOnLogic
      ? (actionLogic as number) // As we are already making sure with nextIsBasedOnLogic that actionLogic is a number
      : getFunnelPageIdByStepIndex(activeStep + 1);

    fireMeetovoTracking(socket, {
      currentPageId,
      nextPageId,
    });

    const nextStepIndex = getStepIndexByFunnelPageId(actionLogic as number);
    if (nextIsBasedOnLogic && nextStepIndex !== -1) {
      addToStepHistory(nextStepIndex);
      goToStep(nextStepIndex);
    } else {
      addToStepHistory(activeStep + 1);
      nextStep().catch(console.error);
    }

    dispatch(setNextPageActionLogic(undefined));
    dispatch(setSectionPageActionLogic(undefined));
  }, [nextPageActionLogic, nextSectionActionLogic]);

  useEffect(() => {
    if (customJs) {
      const fragment = document.createRange().createContextualFragment(customJs);
      document.body.appendChild(fragment);
    }
  }, [funnelPage.craftState]);

  return (
    <>
      <ProgressBar />
      <ResolvedComponentsByCraftState
        compressedCraftState={funnelPage.craftState}
        trackingId={trackingId}
      />
    </>
  );
};

export default Page;
